import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Door, DoorType, DoorPanelVariant, PanelConfig } from '../types';
import { DoorCheck, doorOffset } from '../utils/doorPlacement';
import { selectAllOnFocus } from './dialogKit';

interface DoorDialogProps {
  wallSide: 'left' | 'right' | 'top' | 'bottom';
  wallLength: number;
  door: Door;
  config: PanelConfig;
  onConfirm: (door: Door) => void;
  onDelete?: (doorId: string) => void;
  onCancel: () => void;
  panelThick?: number;
  minPanel?: number;
  check?: (door: Door) => DoorCheck;     // 놓을 수 있는지 + 놓인 빈 구간 (세계 좌표)
  onPreview?: (door: Door | null) => void; // 바꾸는 즉시 도면에 반영
}

/** 문 폭 빠른 선택 — 이전 시스템 DB 문 규격(판넬도아 유형, 높이 1800) 기준 */
const WIDTH_PRESETS = [750, 800, 850, 900, 950, 1000, 1100, 1200, 1300, 1500, 1800];

const sideNames: Record<string, string> = {
  top: '상단 벽', bottom: '하단 벽', left: '좌측 벽', right: '우측 벽'
};

const DOOR_TYPES: { value: DoorType; label: string; desc: string }[] = [
  { value: 'single-right', label: '단문 우', desc: '→ 오른쪽 오픈' },
  { value: 'single-left',  label: '단문 좌', desc: '← 왼쪽 오픈' },
  { value: 'double',       label: '양문',    desc: '↔ 양쪽 오픈' },
];

const PANEL_VARIANTS: { value: DoorPanelVariant; label: string; lt: string; rt: string }[] = [
  { value: 1, label: '1번', lt: '◁', rt: '▷' },
  { value: 2, label: '2번', lt: '▶', rt: '◀' },
  { value: 3, label: '3번', lt: '▶', rt: '▷' },
  { value: 4, label: '4번', lt: '◁', rt: '◀' },
];

const DIALOG_W = 440;

const DoorDialog: React.FC<DoorDialogProps> = ({
  wallSide, wallLength, door, config, onConfirm, onDelete, onCancel,
  panelThick = 0, minPanel = 200, check, onPreview,
}) => {
  const [position, setPosition]   = useState(door.position);
  const [width, setWidth]         = useState(door.width);
  const [leftPanel, setLeftPanel] = useState(door.leftPanel);
  const [rightPanel, setRightPanel] = useState(door.rightPanel);
  const [height, setHeight]       = useState(door.height);
  const [grooveDir, setGrooveDir] = useState(door.grooveDir);
  const [doorType, setDoorType]       = useState<DoorType>(door.doorType);
  // 0 = 자동(흐름): 문을 흐름 속 패널로 보고 들어오는 쪽 음수·나가는 쪽 양수 (엔진이 결정)
  const [panelVariant, setPanelVariant] = useState<DoorPanelVariant | 0>(door.panelVariant ?? 0);

  const [pos, setPos] = useState({ x: 24, y: Math.max(8, window.innerHeight - 770) });
  const dragging = useRef(false);
  const dragStart = useRef({ mx: 0, my: 0, px: 0, py: 0 });

  const totalWidth = leftPanel + width + rightPanel;
  const endPos = position + totalWidth;

  // 위치 기준: 문이 놓인 빈 구간(벽 안쪽 면·하리·다른 문 사이)의 왼/위 끝, 가운데, 오른/아래 끝
  const horiz = wallSide === 'top' || wallSide === 'bottom';
  const loName = horiz ? '왼쪽' : '위쪽', hiName = horiz ? '오른쪽' : '아래쪽';
  const off = doorOffset(wallSide, panelThick);
  const slot = useMemo(() => check?.(door).slot ?? null, [check, door]);
  const [mode, setMode] = useState<'lo' | 'center' | 'hi'>('lo');
  const [dist, setDist] = useState(() => (slot ? off + door.position - slot.lo : 0));
  const placeFrom = (m: typeof mode, d: number, len: number) => (!slot ? off + position
    : m === 'lo' ? slot.lo + d : m === 'hi' ? slot.hi - d - len : Math.round((slot.lo + slot.hi - len) / 2));
  // 기준·거리·폭이 바뀌면 시작 위치 다시 계산
  useEffect(() => {
    if (slot) setPosition(Math.round(placeFrom(mode, dist, totalWidth) - off));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, dist, totalWidth, slot]);
  const switchMode = (m: typeof mode) => {
    if (!slot) return;
    const from = off + position;
    setDist(m === 'lo' ? from - slot.lo : m === 'hi' ? slot.hi - from - totalWidth : 0);
    setMode(m);
  };

  const draft: Door = { ...door, position, width, height, leftPanel, rightPanel, grooveDir, doorType,
    openDir: doorType === 'single-left' ? 'left' : 'right', panelVariant: panelVariant === 0 ? undefined : panelVariant };
  const chk = check?.(draft);
  // 바꾸는 즉시 도면 미리보기
  useEffect(() => {
    onPreview?.(draft);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position, width, height, leftPanel, rightPanel, grooveDir, doorType, panelVariant]);

  const handleConfirm = () => {
    if (chk && !chk.ok) { alert(`여기에는 문을 놓을 수 없습니다: ${chk.reason}`); return; }
    if (!chk) {
      if (position < 0) { alert('문 위치는 0 이상이어야 합니다.'); return; }
      if (endPos > wallLength) {
        alert(`문 영역이 벽면을 벗어납니다.\n벽: ${wallLength}mm / 문 끝: ${endPos}mm`);
        return;
      }
    }
    onConfirm(draft);
  };

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    dragging.current = true;
    dragStart.current = { mx: e.clientX, my: e.clientY, px: pos.x, py: pos.y };
    e.preventDefault();
  }, [pos]);

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    if (!dragging.current) return;
    setPos({ x: dragStart.current.px + (e.clientX - dragStart.current.mx),
              y: dragStart.current.py + (e.clientY - dragStart.current.my) });
  }, []);

  const onMouseUp = useCallback(() => { dragging.current = false; }, []);

  return (
    <div
      style={{ position: 'fixed', left: pos.x, top: pos.y, width: DIALOG_W, zIndex: 1000,
        background: '#fff', borderRadius: 20, boxShadow: '0 12px 40px rgba(0,0,0,0.16)', overflow: 'hidden', userSelect: 'none' }}
      onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
    >
      {/* 타이틀 드래그 핸들 */}
      <div onMouseDown={onMouseDown} style={{
        background: '#fff', color: '#191F28', padding: '16px 20px 8px',
        borderRadius: 0, cursor: 'grab',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span style={{ fontWeight: 'bold', fontSize: 14 }}>문 수정 - {sideNames[wallSide]}</span>
        <button onClick={onCancel} style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', background: '#F2F4F6', color: '#4E5968', fontSize: 14, cursor: 'pointer', flexShrink: 0 }}>✕</button>
      </div>

      <div style={{ padding: 16 }}>
        {/* 미리보기 */}
        <div style={{ marginBottom: 12, padding: 8, background: '#f5f5f5', borderRadius: 6 }}>
          <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>배치 미리보기</div>
          <div style={{ position: 'relative', height: 28, background: '#E8F3FF', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ position: 'absolute', left: `${(position / wallLength) * 100}%`,
              width: `${(leftPanel / wallLength) * 100}%`, height: '100%',
              background: '#FFE0B2', border: '1px solid #F57C00',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: '#E65100' }}>
              {leftPanel}
            </div>
            <div style={{ position: 'absolute', left: `${((position + leftPanel) / wallLength) * 100}%`,
              width: `${(width / wallLength) * 100}%`, height: '100%',
              background: '#C8E6C9', border: '1px solid #3182F6',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#3182F6', fontWeight: 'bold' }}>
              문 {width}
            </div>
            <div style={{ position: 'absolute', left: `${((position + leftPanel + width) / wallLength) * 100}%`,
              width: `${(rightPanel / wallLength) * 100}%`, height: '100%',
              background: '#FFE0B2', border: '1px solid #F57C00',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: '#E65100' }}>
              {rightPanel}
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#999', marginTop: 2 }}>
            <span>0</span><span>{wallLength}mm</span>
          </div>
        </div>

        {/* 문 종류 */}
        <div style={fieldStyle}>
          <label style={dlabelStyle}>문 종류</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {DOOR_TYPES.map(dt => (
              <button key={dt.value} onClick={() => setDoorType(dt.value)} style={{
                flex: 1, padding: '8px 4px', border: doorType === dt.value ? '2px solid #3182F6' : '1px solid #E5E8EB',
                borderRadius: 6, background: doorType === dt.value ? '#E8F3FF' : '#fff',
                cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
              }}>
                <span style={{ fontSize: 13, fontWeight: 'bold', color: '#333' }}>{dt.label}</span>
                <span style={{ fontSize: 10, color: '#888' }}>{dt.desc}</span>
              </button>
            ))}
          </div>
        </div>

        {/* 패널 음양수 타입 */}
        <div style={fieldStyle}>
          <label style={dlabelStyle}>패널 타입 (음양수 — 화면 왼/위 끝 · 오른/아래 끝)</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setPanelVariant(0)} title="흐름 방향에 맞춰 자동 (들어오는 쪽 음수, 나가는 쪽 양수)" style={{
              flex: 1.2, padding: '8px 4px', border: panelVariant === 0 ? '2px solid #3182F6' : '1px solid #E5E8EB',
              borderRadius: 6, background: panelVariant === 0 ? '#E8F3FF' : '#fff',
              cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
            }}>
              <span style={{ fontSize: 13, fontWeight: 'bold', color: '#333' }}>자동</span>
              <span style={{ fontSize: 10, color: '#555' }}>흐름 따라</span>
            </button>
            {PANEL_VARIANTS.map(pv => (
              <button key={pv.value} onClick={() => setPanelVariant(pv.value)} style={{
                flex: 1, padding: '8px 4px', border: panelVariant === pv.value ? '2px solid #3182F6' : '1px solid #E5E8EB',
                borderRadius: 6, background: panelVariant === pv.value ? '#E8F3FF' : '#fff',
                cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
              }}>
                <span style={{ fontSize: 13, fontWeight: 'bold', color: '#333' }}>{pv.label}</span>
                <span style={{ fontSize: 11, color: '#555', letterSpacing: 2 }}>{pv.lt} {pv.rt}</span>
              </button>
            ))}
          </div>
        </div>

        {/* 위치: 기준 + 거리 */}
        <div style={fieldStyle}>
          <label style={dlabelStyle}>위치 {slot ? `(빈 구간 ${slot.hi - slot.lo}mm 안에서)` : '(시작 위치 mm)'}</label>
          {slot ? (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {([['lo', `${loName} 끝에서`], ['center', '가운데'], ['hi', `${hiName} 끝에서`]] as const).map(([m, label]) => (
                <button key={m} onClick={() => switchMode(m)} style={{
                  padding: '6px 8px', fontSize: 12, borderRadius: 8, cursor: 'pointer', whiteSpace: 'nowrap',
                  border: mode === m ? '2px solid #3182F6' : '1px solid #E5E8EB', background: mode === m ? '#E8F3FF' : '#fff',
                }}>{label}</button>
              ))}
              {mode !== 'center' && (
                <input type="number" value={dist} onChange={e => setDist(Number(e.target.value))} onFocus={selectAllOnFocus}
                  style={{ ...dinputStyle, flex: 1 }} step={10} />
              )}
            </div>
          ) : (
            <input type="number" value={position} onChange={e => setPosition(Number(e.target.value))} onFocus={selectAllOnFocus}
              style={dinputStyle} min={0} step={10} />
          )}
        </div>

        {/* 패널 + 문 폭 */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={dlabelStyle}>좌측 패널 (mm)</label>
            <input type="number" value={leftPanel} onChange={e => setLeftPanel(Number(e.target.value))} onFocus={selectAllOnFocus}
              style={dinputStyle} min={0} step={10} />
          </div>
          <div style={{ flex: 2 }}>
            <label style={dlabelStyle}>문 폭 (mm)</label>
            <input type="number" value={width} onChange={e => setWidth(Number(e.target.value))} onFocus={selectAllOnFocus}
              style={dinputStyle} min={500} step={50} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={dlabelStyle}>우측 패널 (mm)</label>
            <input type="number" value={rightPanel} onChange={e => setRightPanel(Number(e.target.value))} onFocus={selectAllOnFocus}
              style={dinputStyle} min={0} step={10} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: -6, marginBottom: 12 }}>
          {WIDTH_PRESETS.map(w => (
            <button key={w} onClick={() => setWidth(w)} style={{
              padding: '3px 8px', fontSize: 11, borderRadius: 10, cursor: 'pointer',
              border: width === w ? '1px solid #3182F6' : '1px solid #E5E8EB', background: width === w ? '#E8F3FF' : '#fafafa',
            }}>{w}</button>
          ))}
        </div>

        {/* 높이 + 개폐 방향 */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={dlabelStyle}>문 높이 (mm)</label>
            <input type="number" value={height} onChange={e => setHeight(Number(e.target.value))}
              style={dinputStyle} min={1000} step={100} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={dlabelStyle}>개폐 방향</label>
            <select value={grooveDir} onChange={e => setGrooveDir(e.target.value as any)} style={dinputStyle}>
              <option value="IN">안쪽 (IN)</option>
              <option value="OUT">바깥쪽 (OUT)</option>
            </select>
          </div>
        </div>

        <div style={{ fontSize: 12, color: '#444', padding: 8, background: '#fff8e1', borderRadius: 8, marginBottom: 12 }}>
          총 영역: <b>{totalWidth}mm</b> (패널 {leftPanel} + 문 {width} + 패널 {rightPanel})
          {chk?.slot && chk.ok && <> &nbsp;/ {loName} {chk.gapLo} · {hiName} {chk.gapHi}mm 남음</>}
        </div>
        {chk && !chk.ok && (
          <div style={{ fontSize: 12, color: '#F04452', padding: 8, background: '#FFEBEE', borderRadius: 8, marginBottom: 12, fontWeight: 'bold' }}>
            ⚠ 놓을 수 없음: {chk.reason} (양옆 남는 패널 최소 {minPanel}mm)
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
          {onDelete && (
            <button onClick={() => {
              if (window.confirm('문을 삭제하시겠습니까?')) onDelete(door.id);
            }} style={deleteBtnStyle}>삭제</button>
          )}
          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
            <button onClick={onCancel} style={cancelBtnStyle}>취소</button>
            <button onClick={handleConfirm} disabled={!!chk && !chk.ok}
              style={{ ...confirmBtnStyle, ...(chk && !chk.ok ? { background: '#bbb', cursor: 'not-allowed' } : {}) }}>적용</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const fieldStyle: React.CSSProperties = { marginBottom: 12 };
const dlabelStyle: React.CSSProperties = { display: 'block', marginBottom: 4, fontSize: 12, fontWeight: 'bold', color: '#555' };
const dinputStyle: React.CSSProperties = { width: '100%', padding: '6px 10px', fontSize: 13, border: '1px solid #E5E8EB', borderRadius: 8, boxSizing: 'border-box' };
const deleteBtnStyle: React.CSSProperties = { height: 38, padding: '0 16px', fontSize: 14, fontWeight: 600, border: 'none', borderRadius: 10, background: '#FFEEF0', color: '#F04452', cursor: 'pointer' };
const cancelBtnStyle: React.CSSProperties = { height: 38, padding: '0 16px', fontSize: 14, fontWeight: 600, border: 'none', borderRadius: 10, background: '#F2F4F6', color: '#333D4B', cursor: 'pointer' };
const confirmBtnStyle: React.CSSProperties = { height: 38, padding: '0 18px', fontSize: 14, fontWeight: 600, border: 'none', borderRadius: 10, background: '#3182F6', color: '#fff', cursor: 'pointer' };

export default DoorDialog;
